import { useEffect, useRef } from 'react'
import { updatesService } from '@/services/UpdatesService'
import type {
  UpdatesFilters,
  UpdatesHandler,
  UpdatesErrorHandler,
} from '@/services/UpdatesService'

export interface UseUpdatesStreamOptions {
  /** Startup scope. Changing this value reopens against the new startup. */
  startupId: string | null | undefined
  /** Server-side filters. Comma-joined into query params. */
  filters?: UpdatesFilters
  /** Stable callback invoked for each matching event. */
  onUpdate: UpdatesHandler
  /** Optional error hook — fires on SSE `onerror` before reconnect. */
  onError?: UpdatesErrorHandler
  /** When false, no subscription is created. Defaults to true. */
  enabled?: boolean
}

/**
 * React wrapper around `UpdatesService`. Owns exactly one subscription per
 * mount. Reopens on startupId change so org-switching tears down the old
 * stream cleanly before the new one opens.
 *
 * Filter / callback identity is intentionally NOT part of the re-subscribe
 * effect dependencies. Subscribers hold onto callback refs; to see a new
 * handler, unmount/remount or change startupId. This keeps the hot path
 * cheap and prevents reconnect storms when parent components re-render.
 */
export function useUpdatesStream({
  startupId,
  filters,
  onUpdate,
  onError,
  enabled = true,
}: UseUpdatesStreamOptions): void {
  const onUpdateRef = useRef(onUpdate)
  const onErrorRef = useRef(onError)
  onUpdateRef.current = onUpdate
  onErrorRef.current = onError

  // Serialize filter contents so the effect re-fires when they materially
  // change, without depending on caller memoization of the filters object.
  const entityTypesKey = filters?.entityTypes?.slice().sort().join(',') ?? ''
  const entityIdsKey = filters?.entityIds?.slice().sort().join(',') ?? ''
  const sinceKey = filters?.since ?? ''
  const onceKey = filters?.once ? '1' : '0'

  useEffect(() => {
    if (!enabled || !startupId) return

    const unsubscribe = updatesService.subscribe(
      startupId,
      {
        entityTypes: filters?.entityTypes,
        entityIds: filters?.entityIds,
        since: filters?.since,
        once: filters?.once,
      },
      (event) => onUpdateRef.current(event),
      (event) => onErrorRef.current?.(event)
    )

    return unsubscribe
    // Filters consumed from the first render of the effect; re-deps via keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startupId, enabled, entityTypesKey, entityIdsKey, sinceKey, onceKey])
}
